import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, ShieldAlert, CheckCircle2, MessageCircle, Flag, CornerDownRight, X, ArrowLeft, ChevronDown } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';
import Pagination from '../components/Pagination';

const REASONS = [
  {
    id: 'off_topic',
    title: 'Off topic',
    description: "Review doesn't pertain to an experience at or with this business"
  },
  {
    id: 'spam',
    title: 'Spam',
    description: 'Review is from a bot, a fake account or contains ads and promotions'
  },
  {
    id: 'conflict_of_interest',
    title: 'Conflict of interest',
    description: "Review is from someone affiliated with the business or a competitor's business"
  },
  {
    id: 'profanity',
    title: 'Profanity',
    description: 'Review contains swear words, has sexually explicit language or details graphic violence or other illegal activity'
  },
  {
    id: 'bullying_or_harassment',
    title: 'Bullying or harassment',
    description: 'Review personally attacks a specific individual'
  },
  {
    id: 'discrimination_or_hate_speech',
    title: 'Discrimination or hate speech',
    description: 'Review has harmful language about an individual or group based on identity'
  },
  {
    id: 'personal_information',
    title: 'Personal information',
    description: 'Contains personal information such as address or phone number'
  },
  {
    id: 'low_quality_info',
    title: 'Low quality information',
    description: 'Review is off-topic, contains ads, or is gibberish or repetitive'
  }
];

const Reviews = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get('page'), 10);
  const initialPage = !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
  const sizeParam = parseInt(searchParams.get('size'), 10);
  const initialSize = !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 10;

  const [activeTab, setActiveTab] = useState('received'); // 'received' or 'sent'
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialSize);

  useEffect(() => {
    const pageParam = parseInt(searchParams.get('page'), 10);
    const p = !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
    const sizeParam = parseInt(searchParams.get('size'), 10);
    const s = !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 10;
    setCurrentPage(p);
    setPageSize(s);
  }, [searchParams]);

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(reviews.length / pageSize);
    if (newPage >= 0 && (totalPages === 0 || newPage < totalPages)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const newParams = new URLSearchParams(searchParams);
      if (newPage > 0) newParams.set('page', String(newPage + 1));
      else newParams.delete('page');
      if (pageSize !== 10) newParams.set('size', String(pageSize));
      else newParams.delete('size');
      setSearchParams(newParams);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    if (newSize !== 10) newParams.set('size', String(newSize));
    else newParams.delete('size');
    setSearchParams(newParams);
  };
  
  // Reply State
  const [replyReviewId, setReplyReviewId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Report Flow States
  const [reportReview, setReportReview] = useState(null);
  const [reportStep, setReportStep] = useState(1); // 1 = Select Reason, 2 = Confirm, 3 = Success
  const [selectedReason, setSelectedReason] = useState(null);
  const [submittingReport, setSubmittingReport] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = activeTab === 'received' 
        ? await vendorService.getReceivedReviews()
        : await vendorService.getSentReviews();
      if (res.success && res.data) {
        const rawReviews = res.data.reviews || [];
        // Ensure newest reviews appear on top
        rawReviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setReviews(rawReviews);
      } else {
        toast.error("Failed to load reviews: " + (res.error || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  const handleReplySubmit = async (reviewId) => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);
    try {
      const res = await vendorService.replyToReview(reviewId, replyText);
      if (res.success) {
        toast.success("Reply posted successfully!");
        setReplyReviewId(null);
        setReplyText('');
        fetchReviews();
      } else {
        toast.error(res.error || "Failed to post reply");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error posting reply");
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleReportSubmit = async () => {
    if (!reportReview || !selectedReason) return;
    setSubmittingReport(true);
    try {
      const res = await vendorService.reportReview(reportReview.id, selectedReason.title, selectedReason.description);
      if (res.success) {
        toast.success("Review reported successfully. Admin will review the claim.");
        setReportStep(3);
        fetchReviews();
        setTimeout(() => {
          closeReportModal();
        }, 1500);
      } else {
        toast.error(res.error || "Failed to file report");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error submitting report");
    } finally {
      setSubmittingReport(false);
    }
  };

  const openReportModal = (review) => {
    setReportReview(review);
    setReportStep(1);
    setSelectedReason(null);
  };

  const closeReportModal = () => {
    setReportReview(null);
    setReportStep(1);
    setSelectedReason(null);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5 text-amber-400">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            fill={star <= rating ? 'currentColor' : 'none'}
            className={star <= rating ? 'text-amber-400' : 'text-gray-200'}
          />
        ))}
      </div>
    );
  };

  // Pagination Calculations
  const totalPages = Math.ceil(reviews.length / pageSize);
  const paginatedReviews = reviews.slice(currentPage * pageSize, (currentPage + 1) * pageSize);



  return (
    <div className="w-full">
      <div className="border-b border-gray-100 pb-4 mb-6">
        <h2 className="text-xl font-bold text-gray-800">Business Reviews</h2>
        <p className="text-xs text-gray-400 mt-1 font-medium font-sans">
          Manage feedback received on your listing and view reviews you have written.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => handleTabChange('received')}
          className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer ${
            activeTab === 'received'
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Received Reviews
        </button>
        <button
          onClick={() => handleTabChange('sent')}
          className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer ${
            activeTab === 'sent'
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Sent Reviews
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="py-20 text-center text-gray-400 font-semibold text-sm">
          {activeTab === 'received' 
            ? "You haven't received any reviews yet." 
            : "You haven't submitted any reviews yet."}
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {paginatedReviews.map((rev) => (
              <div
                key={rev.id}
                className="bg-white border border-gray-200/80 rounded-2xl p-5 shadow-xs transition-all hover:shadow-sm"
              >
                {/* Top Row: Sender Info & Stars */}
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">
                      {activeTab === 'received' ? rev.user_name : rev.company_name}
                    </h4>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                      {activeTab === 'received' ? rev.user_email : `Reviewed on: ${new Date(rev.created_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {renderStars(rev.rating)}
                    {activeTab === 'received' && (
                      <span className="text-[10px] text-gray-400 font-medium font-sans">
                        {new Date(rev.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Review Text */}
                <p className="text-gray-600 text-xs leading-relaxed mt-3 whitespace-pre-line bg-gray-50/50 p-3 rounded-xl border border-gray-100">
                  "{rev.comment}"
                </p>

                {/* Owner Reply Render */}
                {rev.reply && (
                  <div className="mt-4 pl-4 border-l-2 border-teal-200 flex gap-2 items-start text-xs text-gray-600">
                    <CornerDownRight size={14} className="text-[#2ab391] flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold text-teal-800 mr-1.5">Reply from Owner:</span>
                      <span>{rev.reply}</span>
                    </div>
                  </div>
                )}

                {/* Action Buttons (For Received Reviews only) */}
                {activeTab === 'received' && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center gap-3">
                    {/* Left: Reply triggers */}
                    {!rev.reply ? (
                      replyReviewId === rev.id ? (
                        <div className="w-full flex flex-col gap-2">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write your reply..."
                            className="w-full border border-gray-250 rounded-xl p-3 text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391]"
                            rows={2}
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => { setReplyReviewId(null); setReplyText(''); }}
                              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleReplySubmit(rev.id)}
                              disabled={submittingReply || !replyText.trim()}
                              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white bg-[#2ab391] rounded-lg hover:bg-[#23977a] transition-colors disabled:opacity-50 cursor-pointer"
                            >
                              {submittingReply ? "Posting..." : "Submit Reply"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setReplyReviewId(rev.id)}
                          className="flex items-center gap-1.5 text-[11px] font-extrabold text-[#2ab391] hover:text-[#23977a] uppercase tracking-wider cursor-pointer"
                        >
                          <MessageCircle size={13} />
                          Write Reply
                        </button>
                      )
                    ) : (
                      <div />
                    )}

                    {/* Right: Report trigger */}
                    {replyReviewId !== rev.id && (
                      <button
                        onClick={() => openReportModal(rev)}
                        disabled={rev.is_reported}
                        className={`flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider transition-colors cursor-pointer ${
                          rev.is_reported
                            ? 'text-gray-300 cursor-default'
                            : 'text-rose-500 hover:text-rose-600'
                        }`}
                      >
                        <Flag size={13} />
                        {rev.is_reported ? "Reported" : "Report"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {reviews.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(reviews.length / pageSize)}
              pageSize={pageSize}
              totalItems={reviews.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 20, 50, 100]}
              className="mt-6"
            />
          )}
        </>
      )}

      {/* Google Maps Styled Review Report Multi-step Modal */}
      {reportReview && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col relative animate-in fade-in zoom-in-95 duration-200 border border-gray-100 my-auto">
            
            {/* Header */}
            <div className="px-4 sm:px-5 py-3.5 sm:py-4 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
              <div className="flex items-center gap-3">
                {reportStep === 2 && (
                  <button
                    onClick={() => { setReportStep(1); setSelectedReason(null); }}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
                    title="Back to reasons"
                  >
                    <ArrowLeft size={18} />
                  </button>
                )}
                <h3 className="text-sm sm:text-base font-bold text-gray-800 tracking-tight">
                  {reportStep === 1 ? "Why are you reporting this review?" : "Confirm Report"}
                </h3>
              </div>
              <button
                onClick={closeReportModal}
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all cursor-pointer"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-3.5 sm:p-5 overflow-y-auto flex-1 max-h-[calc(85vh-120px)] sm:max-h-[420px]">
              
              {/* Step 1: Radio list of reasons */}
              {reportStep === 1 && (
                <div className="space-y-2.5 sm:space-y-3.5">
                  {REASONS.map((opt) => (
                    <label
                      key={opt.id}
                      className="flex items-start gap-2.5 sm:gap-3 p-3 sm:p-3.5 rounded-xl border border-gray-150 hover:bg-slate-50/50 cursor-pointer transition-all select-none"
                    >
                      <input
                        type="radio"
                        name="report_reason"
                        checked={selectedReason?.id === opt.id}
                        onChange={() => setSelectedReason(opt)}
                        className="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 shrink-0"
                      />
                      <div>
                        <div className="text-xs sm:text-sm font-bold text-gray-800">{opt.title}</div>
                        <div className="text-[10px] sm:text-xs text-gray-500 font-medium leading-relaxed mt-0.5">
                          {opt.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Step 2: Confirmation Slide with illustration */}
              {reportStep === 2 && selectedReason && (
                <div className="flex flex-col items-center text-center py-4 sm:py-6 px-3 sm:px-4 space-y-4 sm:space-y-5">
                  {/* Custom CSS Illustration representing review spam/issue */}
                  <div className="relative w-36 sm:w-40 h-24 sm:h-28 flex items-center justify-center bg-indigo-50/50 rounded-2xl border border-indigo-100/50 overflow-hidden shadow-inner shrink-0">
                    <div className="w-14 sm:w-16 h-10 sm:h-12 bg-emerald-500 rounded-xl flex flex-col justify-between p-2 shadow-md relative z-10 border border-emerald-400">
                      <div className="flex justify-between items-center">
                        <div className="w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white rounded-full"></div>
                        <div className="w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white rounded-full"></div>
                      </div>
                      <div className="h-1.5 sm:h-2 w-full bg-white rounded-sm"></div>
                    </div>
                    {/* Noise icons representing mail/comments */}
                    <div className="absolute top-3 left-4 w-7 h-5 bg-gray-100 rounded border border-gray-250 flex items-center justify-center text-[7px] text-gray-400 font-mono shadow-sm">
                      #@
                    </div>
                    <div className="absolute bottom-3 right-4 w-7 h-5 bg-gray-100 rounded border border-gray-250 flex items-center justify-center text-[7px] text-gray-400 font-mono shadow-sm">
                      #@
                    </div>
                    <div className="absolute top-4 right-6 w-7 h-5 bg-gray-100 rounded border border-gray-250 flex items-center justify-center text-[7px] text-gray-400 font-mono shadow-sm">
                      #@
                    </div>
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <h4 className="text-base sm:text-lg font-black text-gray-900 leading-tight">
                      {selectedReason.title}
                    </h4>
                    <p className="text-[11px] sm:text-xs text-gray-500 leading-relaxed font-semibold max-w-sm">
                      {selectedReason.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3: Success State */}
              {reportStep === 3 && (
                <div className="flex flex-col items-center text-center py-6 sm:py-10 space-y-3 sm:space-y-4">
                  <div className="w-14 sm:w-16 h-14 sm:h-16 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 border border-emerald-100 shrink-0">
                    <CheckCircle2 size={32} className="sm:w-9 sm:h-9" />
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-gray-900">Report Filed!</h4>
                  <p className="text-xs sm:text-sm text-gray-500">Thank you. The report has been sent to support for investigation.</p>
                </div>
              )}
            </div>

            {/* Footer Buttons (For non-success states) */}
            {reportStep < 3 && (
              <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-2.5 sm:gap-3 shrink-0">
                <button
                  type="button"
                  onClick={closeReportModal}
                  className="px-3.5 sm:px-4 py-2 border border-gray-250 rounded-xl text-xs font-bold text-gray-600 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                {reportStep === 1 ? (
                  <button
                    type="button"
                    onClick={() => setReportStep(2)}
                    disabled={!selectedReason}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm cursor-pointer"
                  >
                    Send report
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleReportSubmit}
                    disabled={submittingReport}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                  >
                    {submittingReport ? "Submitting..." : "Submit"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
